import axios from 'axios';
import api from './api';
import { Base64 } from 'js-base64';
import qs from 'qs';
import { getStoredLanguage, persistLanguage } from '../i18n/language';

const languageMap = {
  'zh-CN': 'zh',
  en: 'en',
  jp: 'jp',
  ko: 'kor',
  th: 'th',
  vi: 'vi',
  hi: 'hi',
  ru: 'ru',
  es: 'es',
  fr: 'fr',
  de: 'de',
  it: 'it',
};

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/gateway-api',
  timeout: 10000,
  // Authentication uses the token header. Cross-origin cookies are not needed
  // and would require Access-Control-Allow-Credentials on every API response.
  withCredentials: false,
});

instance.interceptors.request.use(
  (config) => {
    const headers = { ...config.headers };
    const currentLanguage = persistLanguage(getStoredLanguage());
    const backendLanguage = languageMap[currentLanguage] || 'en';
    const token = localStorage.getItem('token');

    headers.lang = backendLanguage;
    headers['Accept-Language'] = currentLanguage;

    if ((config.method === 'post' || config.method === 'put') && !(config.data instanceof FormData)) {
      headers['content-type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
    }

    if (token) {
      headers.token = Base64.encode(token);
    }

    return {
      ...config,
      headers,
      params: {
        ...config.params,
        lang: backendLanguage,
      },
    };
  },
  (error) => Promise.reject(error)
);

function decodePayload(payload) {
  if (typeof payload !== 'string') {
    return payload;
  }

  const trimmed = payload.trim();
  if (!trimmed) {
    return null;
  }

  try {
    const decoded = Base64.decode(trimmed);
    try {
      return JSON.parse(decoded);
    } catch (parseError) {
      return decoded;
    }
  } catch (decodeError) {
    return payload;
  }
}

function normalizeSuccessResponse(response) {
  const responseData = response && response.data;
  const body = responseData && typeof responseData === 'object'
    ? { ...responseData }
    : { data: responseData };
  const hasDataField = Object.prototype.hasOwnProperty.call(body, 'data');

  if (hasDataField) {
    body.data = decodePayload(body.data);
  }

  const numericCode = typeof body.code === 'undefined' ? undefined : Number(body.code);
  const message = body.msg || body.message || '';
  const ok = typeof numericCode === 'undefined' || numericCode === 0 || numericCode === 200;

  const requestUrl = response && response.config && response.config.url;
  const isUserInfoAuthFailure = numericCode === 401 && /\/member\/getUserInfo$/i.test(requestUrl || '');
  if (isUserInfoAuthFailure) {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
  }

  return {
    ...body,
    ok,
    msg: message,
    message,
    raw: response,
    data: hasDataField ? body.data : body,
  };
}

function normalizeErrorResponse(error) {
  const response = error && error.response;
  const responseData = response && response.data;
  const body = responseData && typeof responseData === 'object'
    ? { ...responseData }
    : {};
  const message = body.msg || body.message || error.message || '网络请求失败';
  const hasDataField = Object.prototype.hasOwnProperty.call(body, 'data');

  return {
    ok: false,
    code: typeof body.code !== 'undefined' ? body.code : ((response && response.status) || -1),
    msg: message,
    message,
    data: hasDataField ? decodePayload(body.data) : null,
    raw: error,
  };
}

async function http(option = {}) {
  const method = (option.method || 'get').toLowerCase();
  const requestData = option.data || option.params || {};
  const endpoint = api[option.url] || option.url;

  if (!endpoint) {
    return {
      ok: false,
      code: -1,
      msg: `缺少接口配置: ${option.url}`,
      message: `Missing API configuration: ${option.url}`,
      data: null,
      raw: null,
    };
  }

  try {
    let response;
    if (method === 'get' || method === 'delete') {
      response = await instance[method](endpoint, { params: requestData });
    } else {
      response = await instance[method](endpoint, requestData instanceof FormData ? requestData : qs.stringify(requestData));
    }

    return normalizeSuccessResponse(response);
  } catch (error) {
    return normalizeErrorResponse(error);
  }
}

export default http;
